import {
  extendZodWithOpenApi,
  OpenApiGeneratorV3,
  OpenAPIRegistry,
} from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';
import {
  authResponseSchema,
  authStatusResponseSchema,
  brandingLogoResponseSchema,
  completePublicResponseRequestSchema,
  completePublicResponseResponseSchema,
  emailVerificationRequestSchema,
  emailVerificationRequiredErrorSchema,
  errorResponseSchema,
  healthResponseSchema,
  loginRequestSchema,
  onboardingIntentRequestSchema,
  onboardingIntentResponseSchema,
  onboardingProgressQuerySchema,
  onboardingProgressResponseSchema,
  planFeatureRequiredErrorSchema,
  planLimitReachedErrorSchema,
  quizGradeRequestSchema,
  registerRequestSchema,
  responseListQuerySchema,
  responseListResponseSchema,
  responseQuizResponseSchema,
  signupAttributionRequestSchema,
  signupAttributionResponseSchema,
  successResponseSchema,
  surveyAnalyticsResponseSchema,
  workspaceUsageResponseSchema,
} from './index.js';

extendZodWithOpenApi(z);

export interface GeneratedOpenApiDocument {
  openapi: string;
  info: { title: string; version: string };
  paths: Record<string, unknown>;
  [key: string]: unknown;
}

export function generateOpenApiDocument(): GeneratedOpenApiDocument {
  const registry = new OpenAPIRegistry();
  const json = (schema: z.ZodTypeAny) => ({ 'application/json': { schema } });
  const errorResponses = {
    400: { description: 'Invalid request', content: json(errorResponseSchema) },
    401: { description: 'Authentication failed', content: json(errorResponseSchema) },
  };

  registry.registerPath({
    method: 'get', path: '/api/v1/auth/status', summary: 'Get configured authentication methods',
    responses: { 200: { description: 'Authentication status', content: json(authStatusResponseSchema) } },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/auth/login', summary: 'Sign in with email and password',
    request: { body: { content: json(loginRequestSchema) } },
    responses: { 200: { description: 'Authenticated session', content: json(authResponseSchema) }, ...errorResponses },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/auth/register', summary: 'Register an account',
    request: { body: { content: json(registerRequestSchema) } },
    responses: { 201: { description: 'Created account', content: json(authResponseSchema) }, 400: errorResponses[400] },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/auth/me/attribution', summary: 'Record signup attribution after Google or Apple sign-in',
    description: 'Stored only when the account has no attribution yet and was created within the last 24 hours.',
    request: { body: { content: json(signupAttributionRequestSchema) } },
    responses: { 200: { description: 'Whether the attribution was stored', content: json(signupAttributionResponseSchema) }, ...errorResponses },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/auth/verify-email/confirm', summary: 'Confirm an email address',
    request: { body: { content: json(emailVerificationRequestSchema) } },
    responses: { 200: { description: 'Verified email', content: json(successResponseSchema) }, 400: errorResponses[400] },
  });

  const forbidden = (description: string, ...schemas: z.ZodTypeAny[]) => ({
    description,
    content: json(schemas.length === 1 ? schemas[0]! : z.union(schemas as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]])),
  });
  const workspaceIdParam = z.object({ workspaceId: z.string() });

  registry.registerPath({
    method: 'get', path: '/api/v1/onboarding/progress', summary: 'Onboarding checklist for a workspace',
    description: 'Every step is derived from workspace data. Requires a signed-in workspace member.',
    request: { query: onboardingProgressQuerySchema },
    responses: {
      200: { description: 'Onboarding progress', content: json(onboardingProgressResponseSchema) },
      401: errorResponses[401],
      403: { description: 'Not a member of the workspace', content: json(errorResponseSchema) },
    },
  });
  registry.registerPath({
    method: 'put', path: '/api/v1/onboarding/intent', summary: 'Store what the user is here for',
    request: { body: { content: json(onboardingIntentRequestSchema) } },
    responses: { 200: { description: 'Stored intent', content: json(onboardingIntentResponseSchema) }, ...errorResponses },
  });
  registry.registerPath({
    method: 'get', path: '/api/v1/workspaces/{workspaceId}/usage', summary: 'Current-period usage and plan limits',
    request: { params: workspaceIdParam },
    responses: { 200: { description: 'Usage for the current billing period', content: json(workspaceUsageResponseSchema) }, 401: errorResponses[401] },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/workspaces/{workspaceId}/branding/logo', summary: 'Upload the workspace logo',
    description: 'multipart/form-data with one file field: PNG, JPEG or WebP (no SVG), at most 1 MB. Workspace owners and admins only; available on every plan. The logo is saved to the workspace branding and served from a public, long-cached URL.',
    request: {
      params: workspaceIdParam,
      body: { content: { 'multipart/form-data': { schema: z.object({ file: z.string().openapi({ type: 'string', format: 'binary' }) }) } } },
    },
    responses: {
      200: { description: 'Uploaded logo', content: json(brandingLogoResponseSchema) },
      400: errorResponses[400],
      401: errorResponses[401],
      413: { description: 'File larger than 1 MB', content: json(errorResponseSchema) },
      415: { description: 'Not a PNG, JPEG or WebP image', content: json(errorResponseSchema) },
    },
  });
  registry.registerPath({
    method: 'post', path: '/api/v1/workspaces/{workspaceId}/api-keys', summary: 'Create an API key (example of plan and verification refusals)',
    description: 'Every plan refusal answers 403 with PlanFeatureRequiredError or PlanLimitReachedError; features that send email or expose keys answer 403 EmailVerificationRequiredError for unverified accounts.',
    request: { params: workspaceIdParam },
    responses: {
      403: forbidden('Plan or verification refusal', planFeatureRequiredErrorSchema, planLimitReachedErrorSchema, emailVerificationRequiredErrorSchema),
    },
  });

  registry.registerPath({
    method: 'post', path: '/api/v1/public/workspaces/{workspaceSlug}/surveys/{slug}/responses/complete', summary: 'Complete a public survey response',
    description: 'Stores the answers and finishes the response. Completion is idempotent: repeating it returns success (and the stored quiz result). For a quiz the reply carries `quiz`, limited by the survey\'s quiz settings: `score` only when the score is shown, `questions` (answer key and explanations) only when correct answers are shown. Open-text answers are graded by AI when the server has a key, otherwise they stay pending review.',
    request: {
      params: z.object({ workspaceSlug: z.string(), slug: z.string() }),
      body: { content: json(completePublicResponseRequestSchema) },
    },
    responses: {
      200: { description: 'Completed response', content: json(completePublicResponseResponseSchema) },
      404: { description: 'Response not found', content: json(errorResponseSchema) },
      422: { description: 'Answers failed validation', content: json(errorResponseSchema) },
    },
  });

  const responseIdParam = z.object({ responseId: z.string() });
  const notFound = { description: 'Not found', content: json(errorResponseSchema) };
  const forbiddenError = { description: 'Not allowed', content: json(errorResponseSchema) };
  registry.registerPath({
    method: 'get', path: '/api/v1/surveys/{surveyId}/responses', summary: 'List responses',
    description: 'Workspace members (embedded sessions need results:view). `quizStatus` and `sort` apply to quiz surveys: every graded response carries `quiz` (score, pass result, pending review count), anonymous ones included. The search, date and quizStatus filters are shared with the CSV, XLSX, PDF and ZIP exports, which add score, max score, percent, pass result, pending review and one correct flag per graded question for quiz surveys.',
    request: { params: z.object({ surveyId: z.string() }), query: responseListQuerySchema },
    responses: { 200: { description: 'One page of responses', content: json(responseListResponseSchema) }, 400: errorResponses[400], 403: forbiddenError, 404: notFound },
  });
  registry.registerPath({
    method: 'get', path: '/api/v1/surveys/{surveyId}/analytics', summary: 'Survey analytics',
    description: 'For quiz surveys `quiz` holds the average score, pass rate, a score distribution in 10-point buckets and the questions with the lowest correct rate (confidential answers the viewer may not see are left out of those rates). Null for other surveys.',
    request: { params: z.object({ surveyId: z.string() }) },
    responses: { 200: { description: 'Analytics', content: json(surveyAnalyticsResponseSchema) }, 403: forbiddenError, 404: notFound },
  });
  registry.registerPath({
    method: 'get', path: '/api/v1/responses/{responseId}/quiz', summary: 'Quiz result of a response',
    description: 'Workspace members (embedded sessions need results:view). Per question: status, points, the answer and the answer key as stored and as readable text, explanation, AI feedback and who graded it. Confidential answers are withheld from members who are not the survey author. `quiz` is null when the response has no stored result.',
    request: { params: responseIdParam },
    responses: { 200: { description: 'Quiz result', content: json(responseQuizResponseSchema) }, 403: forbiddenError, 404: notFound },
  });
  registry.registerPath({
    method: 'put', path: '/api/v1/responses/{responseId}/quiz/questions/{questionId}/grade', summary: 'Grade a quiz answer manually',
    description: 'Workspace owners, admins and editors; not available to embedded sessions. Marks the answer correct or incorrect with optional partial points, recomputes the score and pass result and records a workspace audit event (quiz.answer_graded). Typically used for open-text answers pending review; AI or earlier manual grades can be changed too, automatically graded choice answers cannot.',
    request: {
      params: z.object({ responseId: z.string(), questionId: z.string() }),
      body: { content: json(quizGradeRequestSchema) },
    },
    responses: {
      200: { description: 'Updated quiz result', content: json(responseQuizResponseSchema) },
      400: errorResponses[400],
      403: forbiddenError,
      404: notFound,
    },
  });

  for (const [path, status] of [['/health', 'Liveness'], ['/ready', 'Readiness']] as const) {
    registry.registerPath({
      method: 'get', path, summary: `${status} probe`,
      responses: { 200: { description: `${status} status`, content: json(healthResponseSchema) } },
    });
  }

  return new OpenApiGeneratorV3(registry.definitions).generateDocument({
    openapi: '3.0.3',
    info: { title: 'ResponseDock API contracts', version: '1.0.0' },
  }) as GeneratedOpenApiDocument;
}
